import React, { useCallback, useEffect, useState } from 'react';

import { LoadingType } from 'prequalificationV2/actions';
import { setDashboardStep } from 'prequalificationV2/actions/stages';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { hilAutomatedOffersApi, OffersResponseDto } from 'api/AutomatedOffersApi';
import AcceptAutomatedOffer from 'prequalificationV2/pages/PreQualification/steps/AcceptOffer/AutomatedOffers/AcceptAutomatedOffer';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import { OfferDto } from 'api/OffersApi';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { useNavigateAfterUnderwriting } from 'prequalificationV2/hooks/useNavigateAfterUnderwriting';

interface AcceptHilAutomatedOfferProps {
  isPrequal: boolean;
}

const AcceptHilAutomatedOffer = ({ isPrequal }: AcceptHilAutomatedOfferProps) => {
  const dispatch = useAppDispatch();
  const { getAndNavigateToNextStep } = useNavigateAfterUnderwriting();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, variables, applicationStatus } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [currentLoanOffer, setCurrentLoanOffer] = useState<OfferDto | null>(null);
  const [maxLoanAmount, setMaxLoanAmount] = useState<number>(0);
  const [minLoanAmount, setMinLoanAmount] = useState<number>(0);
  const [loanAmount, setLoanAmount] = useState<number>(variables!.loan_amount!);

  const updateLoanOffers = useCallback((loanOffersResponse: OffersResponseDto) => {
    setLoanOffers(loanOffersResponse.loanOffers);
    setMaxLoanAmount(loanOffersResponse.maxLoanAmount);
    setMinLoanAmount(loanOffersResponse.minLoanAmount);

    if (loanAmount > loanOffersResponse.maxLoanAmount) {
      setLoanAmount(loanOffersResponse.maxLoanAmount);
    }
  }, []);

  const handleApplicationRejected = async () => {
    dispatch(setDashboardStep(null));
    await getAndNavigateToNextStep();
  };

  const getLoanOffers = useCallback(async () => {
    try {
      setIsLoading(true);

      const loanOffersResponse = await hilAutomatedOffersApi.getOffers({
        applicationId: applicationId!,
        loanAmount: loanAmount,
      });

      if (loanOffersResponse.isApplicationRejected) {
        await handleApplicationRejected();
      } else {
        updateLoanOffers(loanOffersResponse);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, dispatch, loanAmount, updateLoanOffers]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.Pitched) {
      getLoanOffers();
    }
  }, [applicationStatus, getLoanOffers, loanAmount]);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.Offers,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const handleDeclineOffer = async () => {
    let isSuccess = false;

    try {
      setIsLoading(true);

      await hilAutomatedOffersApi.declineOffer({
        applicationId: applicationId!,
      });

      isSuccess = true;
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }

    return isSuccess;
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    dispatch(setDashboardStep(null));
    await getAndNavigateToNextStep();
  };

  const handleAcceptOffer = async () => {
    try {
      setIsLoading(true);

      const result = await hilAutomatedOffersApi.acceptOffer({
        applicationId: applicationId!,
        loanAmount: loanAmount,
        term: currentLoanOffer!.term,
        lender: currentLoanOffer!.lender,
      });

      if (result.isApplicationRejected) {
        await handleApplicationRejected();
      } else {
        if (currentLoanOffer!.offerUrl) {
          window.open(currentLoanOffer!.offerUrl, '_self');
        } else {
          dispatch(setDashboardStep(null));
          await getAndNavigateToNextStep();
        }
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AcceptAutomatedOffer
        loanAmount={loanAmount}
        loanOffers={loanOffers}
        setLoanAmount={setLoanAmount}
        minLoanAmount={minLoanAmount}
        maxLoanAmount={maxLoanAmount}
        currentLoanOffer={currentLoanOffer}
        setCurrentLoanOffer={setCurrentLoanOffer}
        onCloseDeclineOfferThanksPopup={handleCloseDeclineOfferThanksPopup}
        onDeclineOffer={handleDeclineOffer}
        onAcceptOffer={handleAcceptOffer}
        isLoading={isLoading || isGlobalLoading}
        isPrequal={isPrequal}
      />
    </>
  );
};

export default AcceptHilAutomatedOffer;
